/**
 * Which thread activities belong in the work log the user is looking at.
 *
 * Once a thread has a turn, the work log shows that turn and nothing else, so a
 * finished turn's tool rows do not pile up under the next one. Two kinds of
 * activity are exempt.
 *
 * Bootstrap activities (`worktree.*`, `setup-script.*`) are exempt because they
 * describe the checkout the thread runs in, not a turn, and they stay useful
 * for the whole thread.
 *
 * Turnless failures are exempt because the server can refuse a send before a
 * turn exists. `ProviderCommandReactor` appends `provider.turn.start.failed`
 * with `turnId: null` when the checkout folder is gone, so the strict turn
 * filter dropped the one row that explained why the message got no reply
 * (FOR-36451). A thread with no turns at all rendered the same refusal fine,
 * which is why the FOR-36440 tests missed it.
 *
 * The boundary is the latest turn's start, not its completion. `completedAt` is
 * null while a turn runs, so a completion boundary would hide every turnless
 * error raised during the turn being watched, which is the one moment a work
 * log has to speak. Starting at the turn's start still hides the stale ones: a
 * refusal from an earlier send predates the turn the user then got, so it stays
 * out of the current turn's log.
 */
import { type OrchestrationThreadActivity, type TurnId } from "@cmmd-forge/contracts";

export interface WorkLogTurnScope {
  /** The turn the work log is showing, or undefined when the thread has none. */
  readonly latestTurnId: TurnId | undefined;
  /**
   * When that turn began: `startedAt`, or `requestedAt` for a turn that was
   * asked for but never started. Null when the caller cannot say, which keeps
   * turnless failures filtered.
   */
  readonly latestTurnStartedAt: string | null;
}

export function isInWorkLogTurnScope(
  activity: OrchestrationThreadActivity,
  scope: WorkLogTurnScope,
): boolean {
  if (!scope.latestTurnId) return true;
  if (activity.turnId === scope.latestTurnId) return true;
  if (isBootstrapActivity(activity)) return true;
  return isCurrentTurnlessFailure(activity, scope.latestTurnStartedAt);
}

export function isBootstrapActivity(activity: OrchestrationThreadActivity): boolean {
  return activity.kind.startsWith("worktree.") || activity.kind.startsWith("setup-script.");
}

/**
 * Only `error` tone passes. Turnless info rows keep the behaviour they had:
 * they belong to no turn, and nothing about them is worth interrupting the
 * current turn's log for.
 */
function isCurrentTurnlessFailure(
  activity: OrchestrationThreadActivity,
  latestTurnStartedAt: string | null,
): boolean {
  if (activity.turnId !== null || activity.tone !== "error") return false;
  if (latestTurnStartedAt === null) return false;
  // `>=` so an activity stamped in the same millisecond as the turn start
  // counts as part of it rather than falling in the gap before it.
  return activity.createdAt >= latestTurnStartedAt;
}
